import { HOUR } from 'core/common/constants';
import { Config } from 'core/common/contexts';
import { RequestLimitExceededError } from 'core/common/errors';
import { BaseService, HttpClient } from 'core/common/services';
import { Logger } from 'core/common/services/logger/interfaces';
import { delay } from 'core/common/utils/delay';
import { getLocalization } from 'core/localization/utils/getLocalization';
import {
  CreateOrderParams,
  isActivePaymentStatus,
  Order,
  OrderDataType,
  OrderQuiz,
  PaymentFormData,
  RecurringData,
} from '../../entities';
import {
  OrderDtoMapper,
  OrderQuizDtoMapper,
  PaymentFormDataDtoMapper,
  RecurringDataDtoMapper,
  ResignFormDataDtoMapper,
} from '../../mappers';
import { OrderDTO, PaymentFormDataDto, ResignFormDataDTO } from '../dto';

export class PurchaseService extends BaseService {
  private recurringAttempt = 0;

  constructor(httpClient: HttpClient, logger: Logger, private readonly config: Config) {
    super(httpClient, logger);
  }

  async createPayment(orderId: string): Promise<PaymentFormData> {
    const dtoMapper = new PaymentFormDataDtoMapper();

    const { data } = await this.httpClient.get<PaymentFormDataDto>(
      `/orders/${orderId}/payments-new`,
    );

    return dtoMapper.toEntity(data);
  }

  async createPaymentFromProductName(
    orderId: string,
    productName: string,
  ): Promise<PaymentFormData> {
    const { data } = await this.httpClient.post(`/orders/${orderId}`, {
      data: {
        offer_name: productName,
      },
    });

    return this.createPayment(data.order_id);
  }

  async createOrder(userData: CreateOrderParams): Promise<Order> {
    const localization = getLocalization();
    const dtoMapper = new OrderDtoMapper();

    const { data } = await this.httpClient.post('/orders', {
      data: userData,
      headers: {
        localization,
      },
    });

    return dtoMapper.toEntity(data);
  }

  async getOrder(order: string, params: string): Promise<OrderQuiz> {
    const dtoMapper = new OrderQuizDtoMapper();

    const { data } = await this.httpClient.get<OrderDTO>(`/orders/${order}?${params}`);

    return dtoMapper.toEntity(data);
  }

  async getActivePayments(offerId: string, email: string) {
    const { data } = await this.httpClient.get<Array<{ payment_status: string }>>('/orders', {
      params: {
        offer_id: offerId,
        email,
        created_after: Date.now() - HOUR,
      },
    });

    return data.filter((item) => isActivePaymentStatus(item.payment_status));
  }

  async purchaseReport(reportType: string, orderData?: OrderDataType) {
    try {
      const { data } = await this.httpClient.post('/orders/recurring/async', {
        data: {
          order_data: orderData || {},
          offer_name: reportType,
          project: 'Nebula',
        },
      });

      const result = await this.waitRecurringData(data.order_id);

      return result;
    } catch (err) {
      throw err;
    }
  }

  async getRecurringStatus(orderId: string) {
    const mapper = new RecurringDataDtoMapper();

    const { data } = await this.httpClient.get(`orders/status?order_id=${orderId}`);

    return mapper.toEntity(data);
  }

  async getResignForm(offerId: string, tokenId: string) {
    const dtoMapper = new ResignFormDataDtoMapper();

    const { data } = await this.httpClient.post<ResignFormDataDTO>('/orders/resign-form', {
      data: {
        token_id: tokenId,
        offer_name: offerId,
        project: 'Nebula',
      },
    });

    return dtoMapper.toEntity(data);
  }

  async createOrderByOfferId(offerId: string): Promise<Order> {
    const dtoMapper = new OrderDtoMapper();

    const { data } = await this.httpClient.post<OrderDTO>(`/orders/upsell/${offerId}`);

    return dtoMapper.toEntity(data);
  }

  private async waitRecurringData(orderId: string): Promise<RecurringData> {
    const { recurringPayment } = this.config;
    const { refetchDelay, maxRetryAttempts } = recurringPayment;

    while (this.recurringAttempt < maxRetryAttempts) {
      const result = await this.getRecurringStatus(orderId);

      if (result.status !== 'new') {
        this.clearRecurringAttempt();
        return result;
      }

      this.updateRecurringAttempt();
      await delay(refetchDelay);
    }

    throw new RequestLimitExceededError();
  }

  private clearRecurringAttempt() {
    this.recurringAttempt = 0;
  }

  private updateRecurringAttempt() {
    this.recurringAttempt++;
  }
}
